import { IBufferControllerConfig } from '@bufferController/BufferController.config';
import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { IBufferMetrics, ITimeRange } from '@bufferController/BufferMetrics/IBufferMetrics';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Logger, TLogger } from '@logger/Logger';
import bind from '@utils/decorators/bind';

import { LifecycleEventReporterFactory, TLifecycleEventReporter } from '../../Debugger/LifecycleEventReporter';

enum EStuckType {
  UNKNOWN = 'unknown',
  GAP = 'gap',
  IN_RANGE = 'inRange',
  END_OF_RANGE = 'endOfRange',
}

export class LiveGapController extends ConfigConsumer<IBufferControllerConfig> implements IDebbugable {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public additionalInfo: TDebuggableAdditionalInfo = {};
  public groups: string[] = ['buffer'];

  private _lastPlaybackPosition: number = 0;
  private _videoElement: HTMLVideoElement | null = null;
  private _numberOfDetectedStallCandidates: number = 0;

  private readonly _onBufferMetricsReadyListener: string;
  private readonly _logger: TLogger;
  private readonly _lifecycleEventReporter: TLifecycleEventReporter;

  constructor(eventBus: TEventBus) {
    super('bufferController', eventBus);
    this._logger = Logger(eventBus, 'Doppio - LiveGapController');

    this._lifecycleEventReporter = LifecycleEventReporterFactory('LiveGapController', this._eventBus);

    this._onBufferMetricsReadyListener = this._eventBus.subscribe(
      BufferControllerEvents.bufferMetricsAvailable,
      this._onBufferMetricsReady,
    );
  }

  public setVideoElement(videoElement: HTMLVideoElement): void {
    this._videoElement = videoElement;
  }

  public removeVideoElement(): void {
    this._videoElement = null;
  }

  @bind
  public destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onBufferMetricsReadyListener);
  }

  @bind
  public _handleConfigUpdated(): void {}

  private _checkIfJumpNeeded(metrics: IBufferMetrics): { isJumpNeeded: boolean; stuckType: EStuckType } {
    let isJumpNeeded: boolean = false;
    let stuckType: EStuckType = EStuckType.UNKNOWN;

    const isPlaybackPositionChanged: boolean = this._lastPlaybackPosition !== metrics.currentPlaybackPosition;
    this._lastPlaybackPosition = metrics.currentPlaybackPosition;

    if (!isPlaybackPositionChanged) {
      this._numberOfDetectedStallCandidates += 1;
    } else {
      this._numberOfDetectedStallCandidates = 0;
    }

    const isInGap: boolean = !metrics.bufferedTimeRanges.some(({ isCurrentTimeRange }) => isCurrentTimeRange);

    if (!isPlaybackPositionChanged && this.config.numberOfCandidatesToDetectGap <= this._numberOfDetectedStallCandidates) {
      this._numberOfDetectedStallCandidates = 0;
      this._lifecycleEventReporter('Playback stalled');
      this._logger.info('Playback position didnt changed since last check');

      isJumpNeeded = true;

      stuckType =
        metrics.currentTimeRange && metrics.currentTimeRange.end - metrics.currentPlaybackPosition < this.config.endOfRangeThreshold
          ? EStuckType.END_OF_RANGE
          : EStuckType.IN_RANGE;
    }

    if (isInGap) {
      this._lifecycleEventReporter('Gap detected');
      this._logger.info('Gap detected');
      isJumpNeeded = true;
      stuckType = EStuckType.GAP;
    }

    this.additionalInfo.isInLastBufferedRange = metrics.isInLastBufferedRange;
    this.additionalInfo.isPlaybackPositionChanged = isPlaybackPositionChanged;
    this.additionalInfo.isInGap = isInGap;
    this.additionalInfo.stuckType = stuckType;

    return {
      isJumpNeeded,
      stuckType,
    };
  }

  @bind
  private _onBufferMetricsReady(metrics: IBufferMetrics): void {
    if (!metrics.bufferedTimeRanges.length || this._videoElement?.currentTime === 0 || this._videoElement?.paused) {
      return;
    }

    const { isJumpNeeded, stuckType } = this._checkIfJumpNeeded(metrics);
    if (isJumpNeeded) {
      this._lifecycleEventReporter('Gap detected. Skipping it');

      if ([EStuckType.END_OF_RANGE, EStuckType.GAP].includes(stuckType)) {
        return this._jumpToNextRangeStart(metrics);
      }

      return this._nudgePlayback(metrics);
    }
  }

  private _setNextPlaybackPosition(position: number): void {
    if (!this._videoElement) {
      return;
    }

    this._videoElement.currentTime = position;
  }

  private _jumpToNextRangeStart(metrics: IBufferMetrics): void {
    if (metrics.isInLastBufferedRange || metrics.currentPlaybackPosition >= metrics.bufferedTimeRanges.slice(-1)[0].end) {
      this._nudgePlayback(metrics);
      return;
    }

    const nextRange: ITimeRange | undefined = metrics.bufferedTimeRanges.find(({ start }) => start > metrics.currentPlaybackPosition);

    if (!nextRange) {
      this._nudgePlayback(metrics);
      return;
    }

    this._lifecycleEventReporter(`Jump to next range requested, jumping to ${nextRange.start + 0.01}`);

    this._setNextPlaybackPosition(nextRange.start + 0.01);
  }

  private _nudgePlayback(metrics: IBufferMetrics): void {
    if (metrics.isOnBufferEdge) {
      return;
    }

    const nextPlaybackPosition: number = metrics.currentPlaybackPosition + this.config.nudgePlaybackAmount;

    this._lifecycleEventReporter(`Nudge playback position`);

    this._setNextPlaybackPosition(nextPlaybackPosition);
  }
}
